// src/components/Booking/WeekView.js
import React, { useState, useEffect, useRef } from 'react';
import bookingService from '../../services/bookingService';
import './WeekView.css';

// ✅ closedDates et isExceptionallyClosed viennent du parent (Booking.js)
// Plus besoin de useClosedDates ici → 0 appel API supplémentaire
const WeekView = ({
  salonId,
  onSelectSlot,
  selectedDate,
  selectedTime,
  closedDates = [],
  isExceptionallyClosed = () => false
}) => {
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [slotsData, setSlotsData] = useState({});
  const [loading, setLoading]     = useState(false);

  // Évite les rechargements en boucle quand isExceptionallyClosed change
  const closedDatesRef = useRef(closedDates);
  useEffect(() => {
    closedDatesRef.current = closedDates;
  }, [closedDates]);

  useEffect(() => {
    if (salonId) loadWeekSlots();
  }, [weekStart, salonId]);

  // Recharger une seule fois quand les fermetures arrivent (après le premier chargement)
  const closedDatesLoaded = useRef(false);
  useEffect(() => {
    if (salonId && closedDates.length > 0 && !closedDatesLoaded.current) {
      closedDatesLoaded.current = true;
      loadWeekSlots();
    }
  }, [closedDates]);

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const loadWeekSlots = async () => {
    try {
      setLoading(true);
      const slots = {};

      for (let i = 0; i < 7; i++) {
        const date    = new Date(weekStart);
        date.setDate(weekStart.getDate() + i);
        const dateStr = formatDate(date);

        // ✅ Pas d'appel API pour les jours en fermeture exceptionnelle
        if (closedDatesRef.current.includes(dateStr)) {
          slots[dateStr] = 'CLOSED';
          continue;
        }

        try {
          const response = await bookingService.getAvailableSlots(salonId, dateStr);
          slots[dateStr] = response.success ? (response.slots || []) : [];
        } catch {
          slots[dateStr] = [];
        }
      }

      setSlotsData(slots);
    } finally {
      setLoading(false);
    }
  };

  const goToPreviousDay  = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 1); setWeekStart(d); };
  const goToNextDay      = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 1); setWeekStart(d); };
  const goToPreviousWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const goToNextWeek     = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };

  const canGoPrevious = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return weekStart >= today;
  };

  const getDayName        = (date) => ['Dim','Lun','Mar','Mer','Jeu','Ven','Sam'][date.getDay()];
  const formatDateDisplay = (date) => `${String(date.getDate()).padStart(2,'0')}/${String(date.getMonth()+1).padStart(2,'0')}`;

  const getWeekRange = () => {
    const start  = new Date(weekStart);
    const end    = new Date(weekStart);
    end.setDate(start.getDate() + 6);
    const months = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
    return start.getMonth() === end.getMonth()
      ? `${start.getDate()}-${end.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`
      : `${start.getDate()} ${months[start.getMonth()]} - ${end.getDate()} ${months[end.getMonth()]} ${start.getFullYear()}`;
  };

  const renderWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date    = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      const dateStr = formatDate(date);
      const dayData = slotsData[dateStr];
      const isToday = date.toDateString() === new Date().toDateString();
      const isClosed = dayData === 'CLOSED' || isExceptionallyClosed(dateStr);

      days.push(
        <div key={dateStr} className={`week-day-column${isClosed ? ' week-day-column--closed' : ''}`}>
          <div className={`week-day-header${isToday ? ' today' : ''}`}>
            <div className="week-day-name">{getDayName(date)}</div>
            <div className="week-day-date">{formatDateDisplay(date)}</div>
          </div>

          <div className="week-day-slots">
            {loading ? (
              <div className="week-day-loading"><div className="mini-spinner" /></div>
            ) : isClosed ? (
              <div className="week-day-exceptional-closure">
                🔒
                <span>Fermeture<br />exceptionnelle</span>
              </div>
            ) : !dayData || dayData.length === 0 ? (
              <div className="week-day-closed">Fermé</div>
            ) : (
              dayData.map(slot => (
                <button
                  key={slot.time}
                  className={`week-slot${!slot.available ? ' disabled' : ''}${
                    selectedDate === dateStr && selectedTime === slot.time ? ' selected' : ''
                  }`}
                  onClick={() => onSelectSlot(dateStr, slot.time)}
                  disabled={!slot.available}
                >
                  {slot.time}
                </button>
              ))
            )}
          </div>
        </div>
      );
    }
    return days;
  };

  return (
    <div className="week-view-container">
      <div className="week-view-header">
        <div className="week-view-nav">
          <button className="week-nav-btn" onClick={goToPreviousDay}  disabled={!canGoPrevious()}>← Jour</button>
          <button className="week-nav-btn" onClick={goToPreviousWeek} disabled={!canGoPrevious()}>← Semaine</button>
        </div>
        <div className="week-view-title">📅 {getWeekRange()}</div>
        <div className="week-view-nav">
          <button className="week-nav-btn" onClick={goToNextWeek}>Semaine →</button>
          <button className="week-nav-btn" onClick={goToNextDay}>Jour →</button>
        </div>
      </div>
      <div className="week-view-grid">{renderWeekDays()}</div>
    </div>
  );
};

export default WeekView;